import LinkIcon from "@mui/icons-material/Link";
import * as React from "react";

import { RobotoIconButton } from "@/shared/components/RobotoIconButton";
import { RobotoTooltip } from "@/shared/components/RobotoTooltip";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";

import { useNotification } from "./notifications/useNotification";

interface CopyLinkButtonProps {
  /** URL to copy to clipboard. Must be fully qualified. */
  url: string;
  /** Tooltip text. Defaults to "Copy share link". */
  tooltip?: string;
  /** Size of the icon — small for table rows, medium for headers. */
  size?: "small" | "medium";
  /** Optional click-handler extension — fires after the copy succeeds/fails. */
  onCopied?: (ok: boolean) => void;
}

/**
 * Icon button that copies a URL to the clipboard and confirms with a toast.
 *
 * The toast goes through the app-wide {@link useNotification} dispatcher, which
 * renders every notification in one fixed bottom-right stack. A local MUI
 * `<Snackbar>` would be wrong here even though it's also `position: fixed`: this
 * button lives inside table cells, and an ancestor with a `transform` (which is
 * how AG Grid positions its rows) becomes the containing block for fixed
 * descendants — so the toast would surface next to the clicked icon instead of
 * in the corner.
 *
 * Distinct from ``CopyToClipboardButton`` because it uses a link icon and a
 * toast (vs. that component's content-copy icon and inline tooltip change). The
 * two visual shapes serve different surfaces: this one is for "share this with a
 * colleague" affordances, typically sat in a table row's action column.
 */
export const CopyLinkButton: React.FC<CopyLinkButtonProps> = ({
  url,
  tooltip = "Copy share link",
  size = "small",
  onCopied,
}) => {
  const showNotification = useNotification();

  const copy = async (e: React.MouseEvent) => {
    // Don't trigger row-level onClick handlers when the icon is inside a
    // clickable parent (e.g. a MUI TableRow with onClick).
    e.stopPropagation();
    let ok: boolean;
    try {
      await navigator.clipboard.writeText(url);
      ok = true;
    } catch (err) {
      // Clipboard API rejection (denied permissions, insecure context, etc.)
      // — surface to monitoring instead of being swallowed silently. The
      // toast covers the user-visible side; ErrorMonitoringService gives
      // ops something to grep when "copy doesn't work" tickets land.
      ErrorMonitoringService.captureError(
        err instanceof Error ? err : new Error(String(err)),
        { context: { source: "CopyLinkButton" } },
      );
      ok = false;
    }
    if (ok) {
      showNotification("Link copied");
    } else {
      showNotification("Couldn't copy link", "error");
    }
    onCopied?.(ok);
  };

  return (
    <RobotoTooltip title={tooltip}>
      <span>
        <RobotoIconButton
          onClick={(e) => void copy(e)}
          size={size}
          eventName="CopyLinkClicked"
          eventProperties={{ link: url }}
        >
          <LinkIcon fontSize="inherit" />
        </RobotoIconButton>
      </span>
    </RobotoTooltip>
  );
};
